import {format} from 'date-fns';
import * as R from 'ramda';
import {UnknownServerData} from '@practicegenius/http-clients-js/src/types';
import {pipe} from '@practicegenius/pg-util/src/FunctionUtils';
import {
  objectKeysToCamelCase,
  objectKeysToSnakeCase,
} from '@practicegenius/pg-util/src/ObjectUtils';
import {isNotNil} from '@practicegenius/pg-util/src/TypescriptUtils';
import {parseIsoOrNull} from '@practicegenius/pg-util/src/DateUtils';
import {RegistrationData, Practice} from './types';
import {getContactId} from './utils';

const dateFormat = 'yyyy-MM-dd';

export const transformPractice = pipe<Practice>(objectKeysToCamelCase);

export function outgoingAccount(
  data: Partial<RegistrationData['account'] & Pick<RegistrationData, 'token'>>
) {
  return R.pipe(
    R.pickBy(isNotNil),
    objectKeysToSnakeCase,
    R.evolve({
      birthdate: date => format(date, dateFormat),
    }),
    data => ({
      ...data,
      password_confirmation: data.password,
    })
  )(data);
}

function transformAccount(
  user: UnknownServerData,
  contacts?: UnknownServerData[]
) {
  return {
    ...(objectKeysToCamelCase(user) as RegistrationData['account']),
    birthdate: parseIsoOrNull(user.birthdate),
    contactId: getContactId(user, contacts),
  };
}

type PreConfirmedEmail = {
  practice_id: number;
  contact: string;
};

export function transformPracticeRecord(
  practiceUserRecord: UnknownServerData,
  meta: UnknownServerData = {}
) {
  const {
    primary_email_availability: primaryEmailAvailability = [],
    pre_confirmed_emails: preConfirmedEmails = [],
  } = meta;

  const restrictedEmails = primaryEmailAvailability
    .map(({contact, available}: {contact: string; available: boolean}) =>
      !available ? contact : null
    )
    .filter(Boolean);

  const preConfirmedEmail = (preConfirmedEmails as Array<PreConfirmedEmail>)
    .filter(({contact}) => !restrictedEmails.includes(contact))
    .find(
      ({practice_id}) => practice_id === practiceUserRecord.practice_id
    )?.contact;

  return {
    ...(objectKeysToCamelCase(
      R.omit(['cellphone', 'responsible_cellphone'], practiceUserRecord)
    ) as RegistrationData['practiceUserRecord']),
    birthdate: parseIsoOrNull(practiceUserRecord.birthdate),
    phone: practiceUserRecord.cellphone,
    responsiblePhone: practiceUserRecord.responsible_cellphone,
    email:
      preConfirmedEmail ||
      (!restrictedEmails.includes(practiceUserRecord.email)
        ? practiceUserRecord.email
        : null),
    responsibleEmail: preConfirmedEmail || practiceUserRecord.responsible_email,
  };
}

function transformMeta(item: UnknownServerData) {
  return {
    remainingRequirements: item.registration?.requires,
    registrationCompleted: item.registration?.completed,
    token: item.registration?.token,
    authorization: R.ifElse(
      isNotNil,
      objectKeysToCamelCase,
      R.defaultTo(null)
    )(item.authorization),
  };
}

export function transformRegistration(data: UnknownServerData) {
  const {item, included, meta} = data;

  const transformed = {
    account: transformAccount(item.user, included?.contacts),
    ...transformMeta(item),
  } as Partial<RegistrationData>;

  const practiceUserRecord = included?.practice_records?.find(
    (record: UnknownServerData) => record.id === item.practice_record_id
  );

  if (practiceUserRecord) {
    transformed.practiceUserRecord = transformPracticeRecord(
      practiceUserRecord,
      meta
    );
  }

  if (included?.practices?.[0]) {
    transformed.linkedPractice = transformPractice(included.practices[0]);
  }

  return transformed;
}
